// Agenda-related components for the Live Meeting Runner
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Clock, CheckCircle, SkipForward, Play, RotateCcw, MoreVertical, Users,
  ChevronRight, ChevronDown, PenLine, Save, Loader2,
  Bold, Italic, List, ListOrdered, Strikethrough, Vote, Gavel,
  Paperclip
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { formatDuration } from './utils';

// Simple Rich Text Editor Component using contentEditable
const RichTextEditor = ({ value, onChange, placeholder, readOnly = false }) => {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value || value === '<p></p>' || value === '<br>');

  // Initialize content
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const currentContent = editorRef.current.innerHTML;
      // Only update if the value is different (avoid cursor jumping)
      if (currentContent !== value && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
        setIsEmpty(!value || value === '<p></p>' || value === '<br>');
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const empty = !html || html === '<br>' || html === '<p></p>' || html === '<p><br></p>';
      setIsEmpty(empty);
      onChange(empty ? '' : html);
    }
  }, [onChange]);

  const execCommand = (command, value = null) => {
    if (readOnly) return;
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const isFormatActive = (command) => {
    return document.queryCommandState(command);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className={`p-2 rounded hover:bg-gray-200 transition-colors`}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className={`p-2 rounded hover:bg-gray-200 transition-colors`}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => execCommand('strikeThrough')}
          className={`p-2 rounded hover:bg-gray-200 transition-colors`}
          title="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className={`p-2 rounded hover:bg-gray-200 transition-colors`}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className={`p-2 rounded hover:bg-gray-200 transition-colors`}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
        </div>
      )}
      {/* Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={handleInput}
          onBlur={handleInput}
          className={`p-3 min-h-[120px] outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 ${readOnly ? 'bg-gray-50' : ''}`}
          style={{ wordBreak: 'break-word' }}
        />
        {isEmpty && (
          <div className="absolute top-3 left-3 text-gray-400 pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// AGENDA ITEM TIMER
// =============================================================================

export const AgendaItemTimer = ({ item, isActive, serverElapsed = 0 }) => {
  const [elapsed, setElapsed] = useState(serverElapsed);
  const plannedSeconds = (item.duration_minutes || 15) * 60;
  const totalSpent = (item.time_spent_seconds || 0) + elapsed;
  const isOvertime = totalSpent > plannedSeconds;
  const percentComplete = Math.min(100, (totalSpent / plannedSeconds) * 100);
  
  useEffect(() => {
    if (!isActive) {
      setElapsed(0);
      return;
    }
    
    setElapsed(serverElapsed);
    const interval = setInterval(() => {
      setElapsed(e => e + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isActive, serverElapsed]);
  
  return (
    <div className="flex items-center gap-3">
      {/* Progress bar */}
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-300 ${isOvertime ? 'bg-red-500' : 'bg-purple-500'}`}
          style={{ width: `${Math.min(100, percentComplete)}%` }}
        />
      </div>
      
      {/* Time display */}
      <div className={`font-mono text-sm ${isOvertime ? 'text-red-600' : 'text-gray-600'}`}>
        {formatDuration(totalSpent)} / {formatDuration(plannedSeconds)}
      </div>
    </div>
  );
};

// =============================================================================
// AGENDA ITEM ROW
// =============================================================================

export const AgendaItemRow = ({ 
  item, 
  index, 
  isActive, 
  isChair, 
  onActivate, 
  onComplete, 
  onSkip, 
  onDefer,
  serverElapsed,
  // Note-taking mode props
  noteTakingMode = false,
  notes = '',
  onNotesChange,
  onSaveNotes,
  isSavingNotes = false,
  // Motions props
  motions = [],
  // Attachments
  attachments = [],
  onOpenAttachment,
  onAttachDocument,
  readOnlyNotes = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const [localNotes, setLocalNotes] = useState(notes);
  const [expandedMotionId, setExpandedMotionId] = useState(null);
  
  // Sync local notes when prop changes
  useEffect(() => {
    setLocalNotes(notes);
  }, [notes]);
  
  const getStatusIcon = () => {
    switch (item.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'skipped':
        return <SkipForward className="h-5 w-5 text-gray-400" />;
      case 'deferred':
        return <RotateCcw className="h-5 w-5 text-yellow-500" />;
      case 'in_progress':
        return <Play className="h-5 w-5 text-purple-600 animate-pulse" />;
      default:
        return <span className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs text-gray-400">{index + 1}</span>;
    }
  };
  
  const getStatusBadge = () => {
    switch (item.status) {
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-700">Completed</Badge>;
      case 'skipped':
        return <Badge variant="secondary">Skipped</Badge>;
      case 'deferred':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Deferred</Badge>;
      case 'in_progress':
        return <Badge className="bg-purple-600 text-white">In Progress</Badge>;
      default:
        return null;
    }
  };
  
  const handleNotesChange = (e) => {
    setLocalNotes(e.target.value);
    if (onNotesChange) {
      onNotesChange(item.id, e.target.value);
    }
  };
  
  const handleSaveNotes = () => {
    if (onSaveNotes) {
      onSaveNotes(item.id, localNotes);
    }
  };
  
  const hasNotes = localNotes?.trim()?.length > 0;
  const hasMotions = motions && motions.length > 0;
  const hasAttachments = attachments && attachments.length > 0;
  
  // Helper to get motion result badge
  const getMotionResultBadge = (status) => {
    switch (status) {
      case 'passed':
        return <Badge className="bg-green-100 text-green-700 text-xs">Passed</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700 text-xs">Failed</Badge>;
      case 'open_for_vote':
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs animate-pulse">Voting</Badge>;
      case 'seconded':
        return <Badge className="bg-purple-100 text-purple-700 text-xs">Seconded</Badge>;
      case 'pending':
      case 'moved':
        return <Badge className="bg-gray-100 text-gray-600 text-xs">Pending</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600 text-xs">{status}</Badge>;
    }
  };
  
  return (
    <div 
      className={`border-b border-gray-100 transition-colors ${
        isActive ? 'bg-purple-50 border-l-4 border-l-purple-600' : 'hover:bg-gray-50'
      }`}
      data-testid={`agenda-item-${item.id}`}
    >
      {/* Main row - clickable in note-taking mode */}
      <div 
        className={`p-4 flex items-start gap-3 ${noteTakingMode ? 'cursor-pointer' : ''}`}
        onClick={noteTakingMode ? () => setExpanded(!expanded) : undefined}
      >
        {/* Note-taking mode chevron */}
        {noteTakingMode && (
          <div className="flex-shrink-0 mt-1">
            {expanded ? (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            )}
          </div>
        )}
        
        {/* Status indicator */}
        <div className="flex-shrink-0 mt-1">
          {getStatusIcon()}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-medium ${isActive ? 'text-purple-900' : 'text-gray-900'}`}>
              {item.title}
            </h4>
            {getStatusBadge()}
            {item.item_type && item.item_type !== 'discussion' && (
              <Badge variant="outline" className="text-xs">{item.item_type}</Badge>
            )}
            {/* Note indicator when collapsed */}
            {noteTakingMode && hasNotes && !expanded && (
              <Badge className="bg-purple-100 text-purple-700 text-xs">
                <PenLine className="h-3 w-3 mr-1" />
                Notes
              </Badge>
            )}
            {/* Motions indicator */}
            {hasMotions && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">
                <Gavel className="h-3 w-3 mr-1" />
                {motions.length} Motion{motions.length > 1 ? 's' : ''}
              </Badge>
            )}
            {(hasAttachments || onAttachDocument) && (
              <div className="ml-auto flex items-center gap-1">
                {hasAttachments && attachments.map((doc) => (
                  <TooltipProvider key={doc.document_id || doc.id} delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50 h-7 w-7"
                          aria-label={doc.title || 'Document'}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenAttachment) {
                              onOpenAttachment(doc.document_id || doc.id, doc.title);
                            }
                          }}
                        >
                          <Paperclip className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {doc.title || 'Document'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
                {onAttachDocument && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 h-7 w-7"
                    title="Attach document"
                    aria-label="Attach document"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAttachDocument(item.id);
                    }}
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {item.description && (
            <p className="text-sm text-gray-500 mb-2">{item.description}</p>
          )}
          
          {/* Timer for active or completed items */}
          {(isActive || item.status === 'completed' || item.time_spent_seconds > 0) && (
            <AgendaItemTimer 
              item={item} 
              isActive={isActive}
              serverElapsed={serverElapsed}
            />
          )}
          
          {/* Presenter */}
          {item.presenter_name && (
            <p className="text-xs text-gray-400 mt-2">
              Presenter: {item.presenter_name}
            </p>
          )}

        </div>
        
        {/* Actions - only for chair */}
        {isChair && item.status !== 'completed' && item.status !== 'skipped' && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {isActive ? (
              <div className="flex items-center gap-1">
                <Button 
                  size="sm" 
                  onClick={() => onComplete(item.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Complete
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onDefer(item.id)}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Defer
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSkip(item.id)}>
                      <SkipForward className="h-4 w-4 mr-2" />
                      Skip
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onActivate(item.id)}
                disabled={item.status === 'deferred'}
              >
                <Play className="h-4 w-4 mr-1" />
                Start
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Expanded notes section */}
      {noteTakingMode && expanded && (
        <div className="px-4 pb-4 pl-16 bg-gray-50 border-t border-gray-100">
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 pt-3">
              Discussion Notes
            </label>
            <RichTextEditor
              value={localNotes}
              onChange={(content) => {
                setLocalNotes(content);
                if (onNotesChange) {
                  onNotesChange(item.id, content);
                }
              }}
              placeholder="Record key discussion points, decisions made, concerns raised..."
              readOnly={readOnlyNotes}
            />
            <div className="flex justify-end">
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleSaveNotes}
                disabled={isSavingNotes || readOnlyNotes}
              >
                {isSavingNotes ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save Notes
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Inline Motions Display */}
      {hasMotions && (
        <div className="px-4 pb-3 pl-12 space-y-2" data-testid={`agenda-item-${item.id}-motions`}>
          {motions.map((motion) => (
            <div 
              key={motion.id}
              className="bg-blue-50 border border-blue-100 rounded-lg overflow-hidden"
              data-testid={`motion-${motion.id}`}
            >
              {/* Collapsed view - always visible */}
              <div 
                className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => setExpandedMotionId(expandedMotionId === motion.id ? null : motion.id)}
              >
                <div className="flex-shrink-0">
                  {expandedMotionId === motion.id ? (
                    <ChevronDown className="h-4 w-4 text-blue-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-blue-500" />
                  )}
                </div>
                <Gavel className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-medium text-blue-900 flex-1 truncate">
                  Motion {motion.motion_number}: {motion.title}
                </span>
                {getMotionResultBadge(motion.status)}
              </div>
              
              {/* Expanded view - details */}
              {expandedMotionId === motion.id && (
                <div className="px-3 pb-3 pt-1 border-t border-blue-100 bg-white">
                  <div className="space-y-2 text-sm">
                    {/* Motion text/description */}
                    {motion.motion_text && (
                      <p className="text-gray-700 italic">&ldquo;{motion.motion_text}&rdquo;</p>
                    )}
                    {motion.description && !motion.motion_text && (
                      <p className="text-gray-600">{motion.description}</p>
                    )}
                    
                    {/* Proposer & Seconder */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                      {motion.moved_by_name && (
                        <span>
                          <span className="font-medium">Proposed by:</span> {motion.moved_by_name}
                        </span>
                      )}
                      {motion.seconded_by_name && (
                        <span>
                          <span className="font-medium">Seconded by:</span> {motion.seconded_by_name}
                        </span>
                      )}
                    </div>
                    
                    {/* Vote counts - only show if voting has occurred */}
                    {(motion.status === 'passed' || motion.status === 'failed' || motion.status === 'open_for_vote' || 
                      motion.votes_for > 0 || motion.votes_against > 0 || motion.votes_abstain > 0) && (
                      <div className="flex items-center gap-3 pt-1">
                        <span className="text-xs font-medium text-gray-500">Votes:</span>
                        <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">
                          For: {motion.votes_for || 0}
                        </span>
                        <span className="text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded">
                          Against: {motion.votes_against || 0}
                        </span>
                        <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                          Abstain: {motion.votes_abstain || 0}
                        </span>
                      </div>
                    )}
                    
                    {/* Vote type indicator */}
                    {motion.vote_type && motion.vote_type !== 'voice' && (
                      <div className="text-xs text-gray-500">
                        Vote type: {motion.vote_type.replace('_', ' ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// GENERAL ADMIN INDICATOR
// =============================================================================

export const GeneralAdminIndicator = ({ isActive, seconds }) => {
  return (
    <div className={`p-4 border-b border-gray-100 ${isActive ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
          <Clock className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1">
          <h4 className={`font-medium ${isActive ? 'text-blue-900' : 'text-gray-600'}`}>
            General / Admin Time
          </h4>
          <p className="text-sm text-gray-500">Opening remarks, announcements, transitions</p>
        </div>
        {isActive && (
          <Badge className="bg-blue-600 text-white">Active</Badge>
        )}
        <div className="font-mono text-sm text-gray-500">
          {formatDuration(seconds)}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// QUORUM INDICATOR
// =============================================================================

export const QuorumIndicator = ({ quorum, onClick }) => {
  if (!quorum) return null;
  
  const { required, present, met } = quorum;
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
        met 
          ? 'bg-green-50 text-green-700 hover:bg-green-100' 
          : 'bg-red-50 text-red-700 hover:bg-red-100'
      }`}
    >
      <Users className="h-4 w-4" />
      <span className="font-medium">{present} of {required}</span>
      <Badge className={met ? 'bg-green-500' : 'bg-red-500'}>
        {met ? 'Quorum Met' : 'No Quorum'}
      </Badge>
    </button>
  );
};
